import React from 'react';

import { IconArchive, IconRestore, IconX } from '@tabler/icons-react';
import { Button } from 'ui-kit';

type Props = {
  /** Selected tapes that are active (archivable). */
  activeIds: string[];
  /** Selected tapes that are archived (restorable) — visible under "Show archived". */
  archivedIds: string[];
  onArchive: (ids: string[]) => void;
  onRestore: (ids: string[]) => void;
  onClear: () => void;
};

/**
 * Contextual bulk-action bar for the tape upload history (ENG-2845 follow-up).
 * Zero UI at rest: renders nothing until at least one row is selected. A mixed
 * selection (active + archived) shows both actions, each scoped to its subset.
 */
const TapeSelectionActionBar: React.FC<Props> = ({
  activeIds,
  archivedIds,
  onArchive,
  onRestore,
  onClear,
}) => {
  const total = activeIds.length + archivedIds.length;
  if (total === 0) return null;

  return (
    <div
      data-testid="tape-selection-bar"
      // Fills the header slot (h-full w-full) so it swaps in place of the
      // "TAPES UPLOADED" row without changing its height — see UploadHistory.
      className="flex h-full w-full items-center gap-3 rounded-md border border-pink-500 bg-background-canvas px-3"
    >
      <span className="text-sm text-slate-200">{total} selected</span>
      {activeIds.length > 0 && (
        <Button
          type="primary"
          size="small"
          label="Archive selected tapes"
          data-testid="tape-bulk-archive-button"
          onClick={() => onArchive(activeIds)}
        >
          <span className="flex items-center gap-1">
            <IconArchive size={16} stroke={1.5} />
            Archive {activeIds.length}
          </span>
        </Button>
      )}
      {archivedIds.length > 0 && (
        <Button
          type="secondary_light"
          size="small"
          label="Restore selected tapes"
          data-testid="tape-bulk-restore-button"
          onClick={() => onRestore(archivedIds)}
        >
          <span className="flex items-center gap-1">
            <IconRestore size={16} stroke={1.5} />
            Restore {archivedIds.length}
          </span>
        </Button>
      )}
      <button
        type="button"
        aria-label="Clear selection"
        data-testid="tape-selection-clear"
        onClick={onClear}
        className="ml-auto rounded-md p-1 text-slate-300 hover:bg-slate-600 hover:text-white"
      >
        <IconX size={16} stroke={1.5} />
      </button>
    </div>
  );
};

export default TapeSelectionActionBar;
